import { useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { MENU_LAYOUT, visibleGroups } from '../menuConfig';
import { resourceIcon } from '../menuIcons';
import { roleCenterIcon } from './roleCenterIcons';
import type { Catalog, RoleView } from './roleCenterApi';
import { previewAccess, sectionsFor } from './roleCenterRules';

/**
 * „Zobacz jako” (decyzja użytkownika 22.10.2026, S-22.10-01): menu i sekcje
 * oczami wybranej roli — z jej danych w centrum, bez przelogowania. Menu
 * liczy ta sama reguła co prawdziwe menu (`visibleGroups`), z przełącznikami
 * funkcji zalogowanego; moduły spółek pomijamy (zależą od konta, nie roli).
 */

/** Klucze uprawnień działów przyjęcia zleceń — jak `intakeSides.ts`. */
const INTAKE_SIDE_PERMISSION = { cs: 'INTAKE_CS', administration: 'INTAKE_ADMINISTRATION' } as const;

export const SeeAsPreview = ({
  catalog,
  roles,
  registered,
  flags,
}: {
  catalog: Catalog;
  roles: RoleView[];
  /** Zasoby zarejestrowane w panelu — menu rysuje tylko je. */
  registered: string[];
  /** Przełączniki funkcji zalogowanego. */
  flags: Record<string, boolean>;
}) => {
  const choosable = roles.filter((r) => r.id !== 'API_CLIENT');
  const [roleId, setRoleId] = useState(choosable.find((r) => r.id === 'PLANNER_FTL')?.id ?? choosable[0]?.id ?? '');
  const role = choosable.find((r) => r.id === roleId);
  if (!role) return <Alert severity="info">Brak ról do podglądu.</Alert>;

  const departmentsOn = flags.intakeDepartments === true;
  const groups = visibleGroups(
    MENU_LAYOUT,
    registered,
    role.id,
    () => true,
    (feature) => flags[feature] === true,
    (side) => !departmentsOn || role.id === 'ADMIN' || role.permissions.includes(INTAKE_SIDE_PERMISSION[side]),
    [],
    previewAccess(role),
  );
  const sections = sectionsFor(role, catalog);

  return (
    <Stack spacing={2} data-testid="role-see-as">
      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
        <TextField
          select
          size="small"
          label="Zobacz jako"
          value={role.id}
          onChange={(e) => setRoleId(e.target.value)}
          sx={{ minWidth: 280 }}
          slotProps={{ htmlInput: { 'data-testid': 'role-see-as-select' } }}
        >
          {choosable.map((r) => (
            <MenuItem key={r.id} value={r.id}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                {roleCenterIcon(r.icon)}
                <span>{r.name}</span>
              </Stack>
            </MenuItem>
          ))}
        </TextField>
        <Typography variant="body2" color="text.secondary">
          Podgląd z ustawień centrum — bez przelogowania. O dostępie i tak rozstrzyga serwer.
        </Typography>
      </Stack>
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '260px minmax(0, 1fr)' },
          alignItems: 'start',
        }}
      >
        <Card variant="outlined" data-testid="role-see-as-menu">
          <List dense disablePadding subheader={<ListSubheader>Pozycje menu</ListSubheader>}>
            <ListItem>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <DashboardIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Pulpit" />
            </ListItem>
            {groups.map((g) => (
              <Box key={g.title ?? `bez-tytulu-${g.pozycje[0]?.resource}`}>
                {g.title ? (
                  <Typography variant="overline" sx={{ px: 2, display: 'block', color: 'text.secondary' }}>
                    {g.title}
                  </Typography>
                ) : null}
                {g.pozycje.map((p) => (
                  <ListItem key={p.resource} sx={{ pl: g.title ? 3 : 2 }} data-testid={`role-see-as-item-${p.resource}`}>
                    <ListItemIcon sx={{ minWidth: 32, '& .MuiSvgIcon-root': { fontSize: '1.1rem' } }}>
                      {resourceIcon(p.resource)}
                    </ListItemIcon>
                    <ListItemText primary={p.label} />
                  </ListItem>
                ))}
              </Box>
            ))}
          </List>
        </Card>
        <Card variant="outlined" data-testid="role-see-as-sections">
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
              Sekcje, ekrany i kafelki
            </Typography>
            <Stack spacing={0.75}>
              {sections.visible.map((e) => (
                <Stack key={e.key} direction="row" spacing={1} sx={{ alignItems: 'center' }} data-testid={`role-see-as-visible-${e.key}`}>
                  {roleCenterIcon(e.icon, { color: 'primary' })}
                  <Typography variant="body2">{e.name}</Typography>
                  <Chip size="small" variant="outlined" label={catalog.kinds[e.kind] ?? e.kind} sx={{ height: 18 }} />
                </Stack>
              ))}
            </Stack>
            {sections.hidden.length > 0 ? (
              <>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2, mb: 0.5 }}>
                  Niewidoczne dla tej roli
                </Typography>
                <Stack spacing={0.5}>
                  {sections.hidden.map((e) => (
                    <Stack
                      key={e.key}
                      direction="row"
                      spacing={1}
                      sx={{ alignItems: 'center', color: 'text.disabled' }}
                      data-testid={`role-see-as-hidden-${e.key}`}
                    >
                      {roleCenterIcon(e.icon, { color: 'disabled' })}
                      <Typography variant="body2">{e.name}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </>
            ) : null}
          </CardContent>
        </Card>
      </Box>
    </Stack>
  );
};
